import React from 'react';
import _ from 'lodash';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Autocomplete from 'react-autocomplete';
import { MD5 } from 'object-hash';

import Highcharts from 'highcharts';
import Drilldown from 'highcharts/modules/drilldown';

import expandIcon from '../../../assets/img/zoom-icon-off.svg';
import collapseIcon from '../../../assets/img/zoom-icon-on.svg';
import arrowDownIcon from '../../../assets/img/arrow_down.svg';

import { Holder } from './Holder';
import './PieChartWidget.scss';

import highchartFactory from '../../../util/highcharts';
const highchartUtils = highchartFactory(Highcharts);

const config = {
  chart: {
    backgroundColor: '#fff',
    type: 'pie',
    plotBorderWidth: 0,
    spacing: [0, 0, 0, 0]
  },
  credits: {
    enabled: false
  },
  title: {
    text: ''
  },
  exporting: {
    enabled: false
  },
  legend: {
    enabled: false
  },
  tooltip: {
    useHTML: true,
    shadow: false,
    hideDelay: 0,
    backgroundColor: '#ffffff00',
    borderWidth: 0,
    formatter: function() {},
    style: {
      fontSize: '11px !important',
      fontWeight: 'normal',
      whiteSpace: 'nowrap',
    }
  },
  plotOptions: {
    pie: {
      innerSize: 0,
      allowPointSelect: false,
      cursor: 'pointer',
      borderWidth: 0,
      dataLabels: {
        enabled: false,
        useHTML: true,
        distance: 40,
        connectorWidth: 1,
        //alignTo: 'plotEdges',
        crop: false,
        overflow: 'allow',
        formatter: function () {},
        style: {
          fontSize: '14px',
          fontWeight: 'normal',
          textOutline: 'none'
        }
      },
    }
  },
  drilldown: {
    series: [],
  },
  series: []
};

class PieChartWidget extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
      search: '',
      subset: '',
      subsets: [],
      series: [],
      selected: '',
      isFullscreen: false,
      isLabelsVisible: true,
      hiddenPoints: [],
      hiddenSubsetPoints: {},
      chartInitialized: false
    };
    this.chartRef = React.createRef();
    this.resizeTimeout = null;
  }

  //lifecycle
  componentDidMount() {
    this.initChart();
    this.mapSelected();
  }

  componentDidUpdate(prevProps, prevState) {
    const sameBreakdowns = _.isEqual(prevProps.breakdowns, this.props.breakdowns);
    const sameColors = _.isEqual(prevProps.colors, this.props.colors);
    const sameOptions = _.isEqual(prevProps.options, this.props.options);    
   
    // Initialize chart if not already done and we have data
    if (!this.state.chartInitialized && this.props.breakdowns && this.props.colors) {
      this.initChart();
    }
   
    if (prevState.isFullscreen !== this.state.isFullscreen) {
      this.handleFullscreenResize();
      // Update chart data to apply new dataLabels configuration
      if (this.props.breakdowns && this.props.colors && this.state.selected) {
        this.updateChartData(this.props.breakdowns[this.state.selected] || [], this.props.colors, this.props.formatter);
      }
    }
    
    if (
      this.props.breakdowns && this.props.colors && this.state.selected &&
      (!sameBreakdowns || !sameColors || prevState.selected !== this.state.selected)
    ) {
      this.updateChartData(this.props.breakdowns[this.state.selected] || [], this.props.colors, this.props.formatter);
    }
    if (
      this.props.breakdowns && this.props.options && this.props.selected &&
      (!sameBreakdowns || !sameOptions || prevProps.selected !== this.props.selected)
    ) {
      this.mapSelected();
    }
  }

  componentWillUnmount() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }

  handleFullscreenResize = () => {
    if (!this.chart) return;
    
    const newHeight = this.state.isFullscreen ? undefined : this.state.isLabelsVisible ? 400 : 230;
    const newSize = this.state.isFullscreen ? '90%' : this.state.isLabelsVisible ? '75%' : '85%';    
    const maxWidth = this.state.isFullscreen ? undefined : this.state.isLabelsVisible ? 168 : 200;
    this.performChartResize(newHeight, newSize, maxWidth);
  }

  performChartResize = (height, size, maxWidth) => {
    if (!this.chart) return;
    
    try {
      const container = this.chartRef.current;
      if (container) {
        if (height){
          container.style.height = `${height}px`;
        }
        else{
          container.style.height = '90%';
        } 
        
        if (maxWidth){
          container.style.maxWidth = `${maxWidth}px`;
        }
        else{
          container.style.maxWidth = '100%';
        }        
      }

      this.chart.setSize(null, height, false);
      
      // Keep pie chart at good size and configure labels for many items
      const pieSize = this.state.isFullscreen ? '70%' : size; // Good size for many labels
      const isFullscreen = this.state.isFullscreen
      this.chart.update({
        chart: {
          height: height || '90%'
        },
        plotOptions: {
          pie: {
            size: pieSize,
            center: ['50%', '50%'],
            dataLabels: {
              enabled: isFullscreen
            }
          }
        }
      }, false);      
      
      this.chart.reflow();
    } catch (error) {
      console.error('Error during chart resize:', error);
    }
  }

  mapSelected() {
    const { selected, options, breakdowns } = this.props;
    if (selected && options && breakdowns) {
      const firstBreakdown = _.first(_.keys(breakdowns));
      const option = 
        _.find(options, option => option.id && option.id.includes(selected) && !!breakdowns[option.name]) ||
        _.find(options, option => option.name === firstBreakdown);
      if (option) {
        this.setState({ 
          subset: '',
          search: option.name,
          selected: option.name,
          value: option.id
        }, () => {
          // Update chart data after state is set
          if (this.chart && this.props.breakdowns && this.props.colors) {
            this.updateChartData(this.props.breakdowns[option.name] || [], this.props.colors, this.props.formatter);
          }
        });
      }
    }
  }

  chartDrillDown = (subset) => {
    this.setState({ subset });
  }

  chartDrillUp = () => {
    this.setState({ subset: '' });
  }

  initChart() {
    const { name, formatter } = this.props;

    // Don't initialize if already initialized or if container doesn't exist
    if (this.chart || this.state.chartInitialized) {
      return;
    }

    const container = this.chartRef.current || document.getElementById(name);
    if (!container) {
      // Retry initialization after a short delay if container isn't ready
      setTimeout(() => this.initChart(), 100);
      return;
    }

    try {
      highchartUtils.setDefaults();
      Drilldown(Highcharts);

      const context = this;
      const chartDrillDown = this.chartDrillDown;
      const chartDrillUp = this.chartDrillUp;

      const chartConfig = _.cloneDeep(config);
      chartConfig.chart.height = this.state.isFullscreen ? 500 : 200;
      chartConfig.plotOptions.pie.center = ['50%', '50%'];
      chartConfig.plotOptions.pie.size = this.state.isFullscreen ? '80%' : '75%';
      
      if (formatter) {
        chartConfig.tooltip.formatter = function(ev) {
          return context.tooltipFormatter(this, context, ev);
        };
      }
      
      chartConfig.drilldown = {
        series: [],
        allowPointDrilldown: true
      };
      chartConfig.chart.events = {
        drilldown(e) {
          if (e && e.point && e.point.drilldown) {
            chartDrillDown(e.point.drilldown);
          }
        },
        drillup() {
          chartDrillUp();
        },
        redraw() {
          context.forceUpdate();
        }
      };

      this.chart = new Highcharts.Chart(container, chartConfig);
      this.setState({ chartInitialized: true }, () => {
        // Update chart with data if available after initialization
        if (this.props.breakdowns && this.props.colors && this.state.selected) {
          this.updateChartData(this.props.breakdowns[this.state.selected] || [], this.props.colors, this.props.formatter);
        }
      });
    } catch (error) {
      console.error('Error initializing chart:', error);
    }
  }

  updateChartData(data, colors, formatter) {    
    if (!this.chart || !this.state.chartInitialized) {
      return;
    }

    try {
      // Clear existing series
      while (this.chart.series && this.chart.series.length > 0) {
        this.chart.series[0].remove(false);
      }

      const drilldown = { series: [] };
      const topLevelSeries = {
        type: 'pie',
        name: 'Top Level',
        showInLegend: false,
        colorByPoint: true,
        dataLabels: this.state.isFullscreen ? {
          enabled: true,
          useHTML: true,
          connectorWidth: 2,
          color: 'rgba(0, 0, 0, 0.87)',
          distance: 40,          
          crop: false,
          overflow: 'justify',
          connectorPadding: 10,
          style: {
            fontSize: '16px',
            fontWeight: 'bold',
            textOutline: 'none',
            whiteSpace: 'normal',
            color: 'rgba(0, 0, 0, 0.87)',
            textDecoration: 'none',
            cursor: 'default'
          },
          formatter: function () {
            const formattedValue = formatter ? formatter(this.point) : this.point.y;
            return `<span class="tooltip-label-box" style="color: #001A33"><strong>${this.point.name}:</strong>${formattedValue}</span>`;
          }
        } : {
          enabled: false
        },
        data: _.map(data, d => {
          const point = {
            name: d.name,
            y: d.value,
            color:
              d.name === '(no-option)'
                ? 'rgba(124,181,236, 0.7)' 
                : d.name.indexOf('(Blocked)') > -1 ? 'rgb(236, 33, 50)' : `#${MD5(d.name).substring(0, 6)}`,
            visible: !this.state.hiddenPoints.includes(d.name)
          };

          if (d.subsets && d.subsets.length) {
            point.drilldown = d.name;

            drilldown.series.push({
              name: d.name,
              id: d.name,
              data: d.subsets.map(s => {
                const hiddenSubsetPoints = this.state.hiddenSubsetPoints[d.name] || [];
                return {
                  name: s.name, 
                  y: s.value, 
                  color: colors[s.name],
                  visible: !hiddenSubsetPoints.includes(s.name)
                };
              })
            });
          }

          return point;
        })
      };
      
      this.setState({
        series: topLevelSeries.data,
        subsets: drilldown.series,
      });

      if (this.chart.series) {
        this.chart.addSeries(topLevelSeries, false);
        this.chart.update({ drilldown }, false);
        this.chart.redraw();
      }
    } catch (error) {
      console.error('Error updating chart data:', error);
    }
  }

  handleLegendItemClick = (pointName, isSubset = false) => {
    if (!this.chart || !this.state.chartInitialized) return;
    
    if (isSubset) {
      const currentSubset = this.state.subset;
      const hiddenSubsetPoints = this.state.hiddenSubsetPoints[currentSubset] || [];
      const isCurrentlyHidden = hiddenSubsetPoints.includes(pointName);
      
      let newHiddenSubsetPoints = { ...this.state.hiddenSubsetPoints };
      
      if (isCurrentlyHidden) {
        newHiddenSubsetPoints[currentSubset] = hiddenSubsetPoints.filter(name => name !== pointName);
      } else {
        newHiddenSubsetPoints[currentSubset] = [...hiddenSubsetPoints, pointName];
      }
      
      this.setState({ hiddenSubsetPoints: newHiddenSubsetPoints }, () => {
        if (this.chart.series[0] && this.chart.series[0].data) {
          const point = this.chart.series[0].data.find(p => p.name === pointName);
          if (point) {
            console.log('Setting point visibility to:', isCurrentlyHidden);
            point.setVisible(isCurrentlyHidden, true);
          }
        }
        this.forceUpdate();
      });
    } else {
      if (this.chart.series[0]) {
        const point = this.chart.series[0].data.find(p => p.name === pointName);
        if (point) {
          const isHidden = this.state.hiddenPoints.includes(pointName);
          
          if (isHidden) {
            point.setVisible(true, false);
            this.setState({
              hiddenPoints: this.state.hiddenPoints.filter(name => name !== pointName)
            }, () => {
              this.chart.redraw();
              this.forceUpdate();
            });
          } else {
            point.setVisible(false, false);
            this.setState({
              hiddenPoints: [...this.state.hiddenPoints, pointName]
            }, () => {
              this.chart.redraw();
              this.forceUpdate();
            });
          }
        }
      }
    }
  }

  handleLegendItemHover = (pointName, isSubset = false) => {
    if (!this.chart || !this.chart.series[0] || !this.state.chartInitialized) return;
    
    const series = this.chart.series[0];
    const hoveredPoint = series.data.find(p => p.name === pointName);
    
    if (!hoveredPoint || hoveredPoint.visible === false) return;
    
    // Set the hovered point to hover state and others to inactive
    series.data.forEach(point => {
      if (point.visible !== false) {
        if (point.name === pointName) {
          point.setState('hover');
        } else {
          point.setState('inactive');
        }
      }
    });
  }

  handleLegendItemUnhover = () => {
    if (!this.chart || !this.chart.series[0] || !this.state.chartInitialized) return;
    
    const series = this.chart.series[0];
    
    // Reset all points to normal state
    series.data.forEach(point => {
      if (point.visible !== false) {
        point.setState('normal');
      }
    });
  }
  
  // handlers
  handleResize = () => {
    const isFullscreen = !this.state.isFullscreen;
    this.setState({ isFullscreen });
    if (this.props.onResize) {
      this.props.onResize(isFullscreen);
    }
  }

  handleHideLabels = () => {
    this.setState({ isLabelsVisible: !this.state.isLabelsVisible }, () => {
      this.handleFullscreenResize();
    });
  };

  handleChangeVisibility = (isOpen) => {
    this.setState({
      search: isOpen ? '' : this.state.selected,
      open: isOpen
    });
  }

  handleFieldSelect = (field) => {
    const { onSelect, breakdowns } = this.props;

    this.setState(
      {
        value: field.id === null ? [] : field.id,
        selected: field.id === null ? '' : field.name,
        search: field.id === null ? '' : field.name,
        subset: '',
        hiddenPoints: [],
        hiddenSubsetPoints: {}
      },
      () => {
        this.handleChangeVisibility(false);
        if (this.chart && this.state.chartInitialized && breakdowns) {
          this.updateChartData(breakdowns[this.state.selected] || [], this.props.colors, this.props.formatter);
        }
        if (onSelect) {
          onSelect(field);
        }
      }
    );
  }

  handleChangeBreakdown = (search) => {
    this.setState({ search });
  };

  handleDrillUp = () => {
    if (this.chart && this.state.chartInitialized) {
      this.chart.drillUp();
    }
  }

  handleDrillDown = (name) => {
    if (this.chart && this.state.chartInitialized) {
      const point = this.chart.series[0].data.find(p => p.drilldown === name);
      if (point && point.doDrilldown) {
        point.doDrilldown();
        this.chart.applyDrilldown();
      }
    }
  }

  getLegendData = () => {
    const { subset, subsets, series } = this.state;

    if (subset) {
      const subsetData = _(subsets).filter(d => d.id === subset).flatMap('data').value();
      return subsetData;
    } else {
      return series;
    }
  }

  renderLegendLabel = (d) => {
    const { subset } = this.state;
    const { formatter } = this.props;
    let isHidden = false;
    if (subset) {
      const hiddenSubsetPoints = this.state.hiddenSubsetPoints[subset] || [];
      isHidden = hiddenSubsetPoints.includes(d.name);
    } else {
      isHidden = this.state.hiddenPoints.includes(d.name);
    }

    let point = d;
    if (formatter && this.chart && this.chart.series && this.chart.series[0] && this.chart.series[0].data) {
      const chartPoint = this.chart.series[0].data.find(p => p.name === d.name);
      if (chartPoint) {
        const currentSeriesData = this.chart.series[0].data;
        const visibleData = currentSeriesData.filter(p => p.visible !== false);
        const total = visibleData.reduce((sum, p) => sum + p.y, 0);

        if (total > 0 && !isHidden) {
          point.percentage = (d.y / total) * 100;
          point.total = total;
        } else {
          point.percentage = 0;
          point.total = total;
        }

        point = {
          ...d,
          y: chartPoint.y,
          color: chartPoint.color
        };
      }
    }
    
    return (
      <div
        key={d.name}
        className={`legend-label ${isHidden ? 'legend-hidden' : ''}`}
        onClick={() => {
          if (subset) {
            this.handleLegendItemClick(d.name, true);
          } else {
            this.handleLegendItemClick(d.name, false);
          }
        }}
        onMouseEnter={() => !isHidden && this.handleLegendItemHover(d.name, !!subset)}       
        style={{ 
          opacity: isHidden ? 0.4 : 1,
          cursor: 'pointer',
          transition: 'opacity 0.2s ease'
        }}
      >
        <span 
          className="circle" 
          style={{
            backgroundColor: isHidden ? '#ccc' : d.color,
            transition: 'background-color 0.2s ease'
          }}
        ></span> 
        <span 
          className="label"
          style={{
            color: isHidden ? '#999' : 'inherit',
            textDecoration: isHidden ? 'none' : 'none',
            transition: 'color 0.2s ease'
          }}
        >
          {this.state.isFullscreen ? <span>{d.name} { formatter ? formatter(point || d) : d.name }</span> : <span>{d.name}</span>}
        </span>
      </div>
    );
  }
  
  tooltipFormatter(d) {
    const { formatter } = this.props;
    
    let point;
    if (formatter && this.chart && this.chart.series && this.chart.series[0] && this.chart.series[0].data) {
      point = this.chart.series[0].data.find(p => p.name === d.key);
    }
    return `<div class="pie-tooltip"><span class="circle" style="background-color: ${d.color}";></span><span>${point.name}&nbsp;</span> <span>${ formatter ? formatter(point || d) : d.key }</span></div>`;
  }

  render() {
    const { isFullscreen, selected, search, open, subset } = this.state;
    const { options, title, name, className = '' } = this.props;

    const legendData = this.getLegendData();
    const hasChartData = !!legendData && !!legendData.length;

    return (
      <div className={`widget ${className}${ isFullscreen ? ' fullscreen' : ''}${ !hasChartData ? ' normal' : ''}`}>
        <div className='cell-header'>
          <div className='cell-title'>
            <h4>{title}</h4>
            <OverlayTrigger
              placement="left"
              overlay={
                <Tooltip id="labelsTooltip" className="tooltip_dark">
                  {this.state.isLabelsVisible ? 'Hide labels' : 'Show labels'}
                </Tooltip>
              }>
              <div className="labels-toggle" onClick={this.handleHideLabels}>
                <img
                  src={arrowDownIcon}
                  alt="toggle labels"
                  style={{ transform: this.state.isLabelsVisible ? 'rotate(180deg)' : 'rotate(0deg)' }}
                />
              </div>
            </OverlayTrigger>
          </div>
          <div className='header-actions'>
            <Button variant="flat" onClick={this.handleResize}>
              <img src={isFullscreen ? expandIcon : collapseIcon} alt="toggleFullScreen" />
            </Button>
          </div>
        </div>
        
        <div className="cell-content pie-widget">
          <Holder>
            {this.state.isLabelsVisible && (
              <div className="pie-widget-left">
                <div className="pie-widget-legend-container"  style={{ paddingLeft: isFullscreen ? '20px' : '0'}} onMouseLeave={() => this.handleLegendItemUnhover()}>
                  {_.map(legendData, (d) => this.renderLegendLabel(d))}
                </div>
              </div>
            )}
            <div className="pie-widget-right">
              <div 
                ref={this.chartRef}
                id={name} 
                className="chart-container"
                style={{
                  height: isFullscreen ? '500px' : '400px',
                  width: '100%'
                }}
              />
              
              { subset ? (
                <div className='chart-navigation-box'>
                  <span onClick={this.handleDrillUp}>{selected} /</span>
                  <span>&nbsp;{subset}</span>
                </div>
              ) : (
                <div className='chart-box'>
                  <div className='search-box'>
                    <Autocomplete
                      getItemValue={item => item.name}
                      items={options}
                      renderInput={props => <input placeholder="Breakdown by" {...props} />}
                      renderMenu={(items, value, style) => {
                        return items.length ? (
                          <div id="menu" style={{ ...style, ...this.menuStyle }} children={items} />
                        ) : (
                          <div className='no-items-found'>
                            <span>No custom fields found</span>
                          </div>
                        );
                      }}
                      renderItem={(field, isHighlighted) => (
                        <div
                          key={field.name}
                          className='dropdown-list'
                          style={{
                            background: isHighlighted ? '#E6F1F7' : '',
                            fontStyle: field.id === null ? 'italic' : 'normal'
                          }}
                        >
                          <div
                            id={field.name}
                            className={`dropdown-item${field.name === selected ? 'selected' : ''}`}
                          >
                            {field.name}
                          </div>
                        </div>
                      )}
                      value={search}
                      onChange={(ev, value) => this.handleChangeBreakdown(value)}
                      onSelect={(value, field) => this.handleFieldSelect(field)}
                      shouldItemRender={(field, value) => {
                        return field.name.toLowerCase().indexOf(value.toLowerCase()) !== -1;
                      }}
                      open={open}
                      onMenuVisibilityChange={open => this.handleChangeVisibility(open)}
                    />
                  </div>
                </div>
              )}
            </div>
          </Holder>
        </div>
      </div>
    );
  }
}

export { PieChartWidget };